/**
 * Точки параметрических многоугольников редактора табло. Фигура лежит в рамке `width × height` от `(x, y)`;
 * параметр меняет только форму внутри рамки — размер `Polygon` (`width` / `height` / `pathOffset`, которые Fabric 5
 * считает лишь в `initialize`) остаётся верным.
 */

export interface ShapePoint {
  x: number;
  y: number;
}

export interface ShapeFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

const NO_SCALE = { x: 1, y: 1 };

/** Предел угла скоса: ближе к 90° верхняя и нижняя стороны схлопываются в точку. */
export const MAX_SKEW_ANGLE = 89;

/**
 * Параллелограмм с наклоном боковых сторон `angle` градусов от вертикали: плюс — верх сдвинут вправо, минус — влево,
 * 0 — прямоугольник. Угол — на экране: `scale` растянутого объекта учтён. Сдвиг не больше ширины рамки — иначе стороны
 * вывернутся.
 */
export function parallelogramPoints(frame: ShapeFrame, angle: number, scale = NO_SCALE): ShapePoint[] {
  const { x, y, width, height } = frame;
  const tan = Math.tan((clamp(angle, -MAX_SKEW_ANGLE, MAX_SKEW_ANGLE) * Math.PI) / 180);
  const raw = (height * scale.y * tan) / scale.x;
  const shift = clamp(raw, -width, width);
  const top = Math.max(shift, 0);
  const bottom = Math.max(-shift, 0);
  return [
    { x: x + top, y },
    { x: x + width - bottom, y },
    { x: x + width - top, y: y + height },
    { x: x + bottom, y: y + height },
  ];
}

/**
 * Наибольший угол скоса, при котором сдвиг ещё влезает в рамку (верх или низ схлопывается в точку), целые градусы
 * вниз. Без него поле показало бы 89°, а фигура упёрлась бы в ширину на меньшем угле.
 */
export function maxParallelogramAngle(frame: ShapeFrame, scale = NO_SCALE): number {
  if (!frame.height) return 0;
  const limit = (Math.atan((frame.width * scale.x) / (frame.height * scale.y)) * 180) / Math.PI;
  return Math.min(MAX_SKEW_ANGLE, Math.floor(limit));
}

/** Угол скоса параллелограмма из его точек (порядок — как у `parallelogramPoints`); `scale` — масштаб объекта. */
export function readParallelogramAngle(points: ShapePoint[], scale = NO_SCALE): number {
  const { height } = frameOf(points);
  if (points.length !== 4 || !height) return 0;
  const shift = (points[0].x - points[3].x) * scale.x;
  return (Math.atan(shift / (height * scale.y)) * 180) / Math.PI;
}

/**
 * Прямоугольник со срезанными под 45° (на экране) углами. Срез задаётся отдельно по осям: у растянутого объекта
 * `cutX ≠ cutY` в своих координатах дают ровный срез на канве. Срез не больше половины стороны.
 */
export function chamferedRectPoints(frame: ShapeFrame, cutX: number, cutY = cutX): ShapePoint[] {
  const { x, y, width, height } = frame;
  const cx = clamp(cutX, 0, width / 2);
  const cy = clamp(cutY, 0, height / 2);
  return [
    { x: x + cx, y },
    { x: x + width - cx, y },
    { x: x + width, y: y + cy },
    { x: x + width, y: y + height - cy },
    { x: x + width - cx, y: y + height },
    { x: x + cx, y: y + height },
    { x, y: y + height - cy },
    { x, y: y + cy },
  ];
}

/** Срез прямоугольника по горизонтали в его координатах (порядок точек — как у `chamferedRectPoints`). */
export function readChamferCut(points: ShapePoint[]): number {
  if (points.length !== 8) return 0;
  return points[0].x - frameOf(points).x;
}

/** Правильный восьмиугольник, вписанный в квадрат `size`: срез — сторона / (2 + √2). */
export function octagonPoints(size: number): ShapePoint[] {
  return chamferedRectPoints({ x: 0, y: 0, width: size, height: size }, size / (2 + Math.SQRT2));
}

/** Рамка точек — в тех же координатах, что и точки `Polygon`. */
export function frameOf(points: ShapePoint[]): ShapeFrame {
  if (!points.length) return { x: 0, y: 0, width: 0, height: 0 };
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
